import { useEffect, useMemo, useRef, useState } from 'react'
import { Faq } from '../components/Faq'
import { LaneTimeline } from '../components/LaneTimeline'
import { PortInput } from '../components/PortInput'
import { ReliabilityBar } from '../components/ReliabilityBar'
import { SailingCard, type SailingCardData } from '../components/SailingCard'
import faqs from '../data/faqs.json'
import { flagFromUnlocode } from '../lib/flag'
import { KEYED_BASE, PUBLIC_BASE } from '../lib/publicApi'
import { shortDate } from '../lib/site-dates'
import { usePageMeta } from '../lib/usePageMeta'

// 24-grid stroke glyphs in currentColor — the brand book bans emoji as UI. 1em, inline, aria-hidden.
const gp = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  style: { display: 'inline-block', verticalAlign: '-0.125em', flexShrink: 0 },
}
const IconStar = ({ filled }: { filled?: boolean }) => (
  <svg {...gp} fill={filled ? 'currentColor' : 'none'} aria-hidden role="presentation">
    <path d="M12 3.5l2.6 5.27 5.82.85-4.21 4.1.99 5.79L12 16.98l-5.2 2.53.99-5.79-4.21-4.1 5.82-.85L12 3.5Z" />
  </svg>
)
const IconLink = () => (
  <svg {...gp} aria-hidden role="presentation">
    <path d="M9 15l6-6M10.5 6.5l1-1a4 4 0 0 1 5.66 5.66l-1 1M13.5 17.5l-1 1a4 4 0 0 1-5.66-5.66l1-1" />
  </svg>
)
const IconCheck = () => (
  <svg {...gp} aria-hidden role="presentation">
    <path d="M20 6L9 17l-5-5" />
  </svg>
)
const IconDownload = () => (
  <svg {...gp} aria-hidden role="presentation">
    <path d="M12 3v12m0 0l4-4m-4 4l-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </svg>
)

type SortKey = 'rel' | 'count' | 'next' | 'fastest'

// Visible Q&A mirrored into FAQPage JSON-LD (src/data/faqs.json, shared with the prerender).
// Methodology only — no fabricated per-lane figures.
const COMPARE_FAQ = faqs.compare

// Free vessels shown before the upgrade gate on the Compare page.
const FREE_VESSELS = 12

// Live, public reliability surface. The marketing Compare page queries the real engine
// (no auth) instead of a hardcoded demo set, so figures are truthful — thin lanes are shown
// flagged "provisional" rather than hidden, and example buttons are real covered lanes.
// PUBLIC_BASE lives in lib/publicApi so the homepage hero card hits the same endpoints.

// What the site's proxy passes on per carrier (apps/web/site-api.js): the on-time % and nothing else
// of the reliability answer (owner 07.10). Delays, sample size and berth precision are keyed.
interface LaneCarrier {
  carrier_code: string
  carrier_name: string
  on_time_pct: number | string
  provisional: boolean
}

interface CoveredLane {
  origin: string
  destination: string
  // Port names from /public (locode itself when a port has no name row) — a lane has to be readable
  // to someone who does not know the codes by heart.
  origin_name: string
  destination_name: string
  carriers: number
  obs: number
}

// A lane reads NAME first with the UN/LOCODE kept alongside in mono. The code stays visible on purpose:
// it is the canonical key behind deep links and saved lanes, and forwarders do quote it. What it can no
// longer be is the only thing on screen.
function LaneLabel({
  origin,
  destination,
  originName,
  destinationName,
  flags = false,
  stack = false,
}: {
  origin?: string | undefined
  destination?: string | undefined
  originName?: string | undefined
  destinationName?: string | undefined
  flags?: boolean
  // Chips are narrow, so they set the names and the codes on two lines instead of one.
  stack?: boolean
}) {
  if (!origin || !destination) return null
  const o = originName || origin
  const d = destinationName || destination
  const unnamed = o === origin && d === destination
  const names = (
    <span className={unnamed ? 'font-mono' : undefined}>
      {flags && `${flagFromUnlocode(origin)} `}
      {o} <span className="text-[var(--text-muted)]">→</span>{' '}
      {flags && `${flagFromUnlocode(destination)} `}
      {d}
    </span>
  )
  // When neither port has a name on file the label already IS the pair of codes, so the second line
  // would just repeat it.
  const codes = unnamed ? null : (
    <span className="font-mono text-[0.85em] text-[var(--text-muted)]">
      {origin} → {destination}
    </span>
  )
  return (
    <span
      className={
        stack
          ? 'flex flex-col items-start leading-tight'
          : 'inline-flex flex-wrap items-baseline gap-x-1.5'
      }
      title={`${o} → ${d} (${origin} → ${destination})`}
    >
      {names}
      {codes}
    </span>
  )
}
// Fold freshly-seen locode → name pairs into the page's name map, keeping the previous object identity
// when nothing is new so a repeat fetch can't trigger a pointless re-render.
function mergeNames(
  prev: Record<string, string>,
  entries: [string | undefined, string | undefined][]
): Record<string, string> {
  let next = prev
  for (const [code, label] of entries) {
    if (code && label && next[code] !== label) next = { ...next, [code]: label }
  }
  return next
}

/**
 * The whole window, not the first page of it.
 *
 * /public/schedules pages: 100 sailings by default, 500 at most, and a `next` link to walk. This
 * page used to get everything the old 200-row cap allowed, so without following the cursor a busy lane
 * would quietly lose its far half again — the exact loss paging was added to fix. Four pages of 500 is
 * 2,000 sailings, well past the busiest lane in a ten week window, and the loop stops the moment the
 * cursor comes back null.
 */
type SchedulesEnvelope = {
  origin?: string
  origin_name?: string
  destination?: string
  destination_name?: string
  sailings?: Sailing[]
  count?: number
  /** The next request, built. `truncated` and `next_cursor` are gone: the flag carried no fact the
   *  link does not, and a caller should not have to learn what a cursor is to read the rest. */
  next?: string | null
  via_port_names?: Record<string, string>
}

async function walkSchedules(qs: string): Promise<{ data: SchedulesEnvelope } | null> {
  let first: SchedulesEnvelope | null = null
  let last: SchedulesEnvelope | null = null
  const all: Sailing[] = []
  const hubNames: Record<string, string> = {}
  // The first request we build; every one after it the API hands us ready-made.
  //
  // `weeks` IS DROPPED IF THE API REFUSES IT, rather than left to blank the page. Web and API deploy
  // separately, so for one release the live bundle can be asking for a window the new API does not
  // list, or the new bundle asking one the old API does not — it has happened in both directions in a
  // single week (10 was withdrawn, 12 was added). A 400 here used to leave `body.data` undefined, the
  // walk broke at page zero, and Compare rendered reliability beside an empty sailings table with
  // nothing on screen to say why. The window we get is then the API's default, which is a narrower
  // answer and not a wrong one.
  let url: string | null = `${KEYED_BASE}/schedules?${qs}&weeks=12&limit=500`
  let droppedWeeks = false
  for (let page = 0; page < 4 && url; page++) {
    // Contained per page. One request is up to four, and a 502 or a truncated body on page three used
    // to reject the whole Promise.all this sits in, blanking reliability and the vessel cards too and
    // throwing away the 1,500 sailings already in hand. Keep what we have and stop.
    let body: { data?: SchedulesEnvelope } | null = null
    try {
      body = (await fetch(url).then((r) => r.json())) as { data?: SchedulesEnvelope }
    } catch {
      break
    }
    if (!body?.data) {
      const code = (body as { error?: { code?: string } } | null)?.error?.code
      if (code === 'INVALID_WEEKS' && !droppedWeeks && page === 0) {
        droppedWeeks = true
        url = `${KEYED_BASE}/schedules?${qs}&limit=500`
        continue
      }
      break
    }
    if (!first) first = body.data
    last = body.data
    for (const s of body.data.sailings ?? []) all.push(s)
    // Hub names are resolved per response, so a hub that only appears on page three has its name only
    // on page three.
    Object.assign(hubNames, body.data.via_port_names ?? {})
    url = body.data.next ?? null
  }
  if (!first) return null
  // The envelope describes what is being returned: the lane from the first page, a count that matches
  // the sailings actually in hand, and the last page's `next` — non-null means the walk stopped at its
  // own four-page bound rather than at the end of the window.
  return {
    data: {
      ...first,
      sailings: all,
      count: all.length,
      via_port_names: hubNames,
      next: last?.next ?? null,
    },
  }
}

interface Sailing {
  carrier_code: string
  carrier_name: string
  service_name: string | null
  vessel_name: string | null
  voyage_number: string | null
  published_departure: string | null
  published_arrival: string | null
  transit_days: number | null
  cy_cutoff: string | null
  /** Why a null cy_cutoff is null. Absent on responses from an older API. */
  cutoff_status?: 'published' | 'not_published' | 'not_held'
}
// One formatter for the whole site (lib/site-dates). This used to read the BROWSER locale, so the
// NEXT DEPARTURE column printed "9 сент." under an English heading for a reader on a Russian machine.
const fmtDate = shortDate

const num = (v: number | string | null | undefined) =>
  v == null ? null : typeof v === 'number' ? v : Number.parseFloat(v)

export function ComparePage() {
  usePageMeta(
    'Compare ocean carrier lanes · SchedulesMCP',
    'Compare carriers on any ocean lane by transit time and real on-time reliability before you book.'
  )
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [rows, setRows] = useState<LaneCarrier[]>([])
  const [covered, setCovered] = useState<CoveredLane[]>([])
  // UN/LOCODE → port name, accumulated from every /public response this page has seen (covered lanes,
  // then whichever lane is queried). A saved or deep-linked lane we have no name for keeps its locode.
  const [portNames, setPortNames] = useState<Record<string, string>>({})
  const portName = (code?: string) => (code ? (portNames[code] ?? code) : '')
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [queried, setQueried] = useState<{ o: string; d: string } | null>(null)
  const [sailings, setSailings] = useState<Sailing[]>([])
  const [sailingsFailed, setSailingsFailed] = useState(false)
  const [cards, setCards] = useState<SailingCardData[]>([])
  const [laneCap, setLaneCap] = useState<{
    total_teu: number | null
    teu_per_week: number | null
    estimated: boolean
  } | null>(null)
  const [blanks, setBlanks] = useState<
    { service: string; after: string; next: string; gap_days: number }[]
  >([])
  // Examples are a nudge, not a directory — show one line by default, expandable.
  const [showAllLanes, setShowAllLanes] = useState(false)
  // Pin-to-compare: vessels the user selects for a side-by-side card.
  const [pinned, setPinned] = useState<SailingCardData[]>([])
  // The Compare tray can be dismissed (✕) without losing the pins; pinning a new vessel re-opens it.
  const [compareDismissed, setCompareDismissed] = useState(false)
  // Include carrier identity (IMO + first partner) so distinct no-vessel placeholder cards on the same
  // departure — which the cards endpoint keeps separate by carrier+voyage — don't collapse to one key.
  const cardKey = (c: SailingCardData) =>
    `${c.vessel_imo ?? ''}-${c.partners[0]?.carrier_code ?? ''}-${c.vessel_name ?? ''}-${c.published_departure}-${c.voyage_number}`
  const isPinned = (c: SailingCardData) => pinned.some((p) => cardKey(p) === cardKey(c))
  function togglePin(c: SailingCardData) {
    setPinned((prev) => {
      if (prev.some((p) => cardKey(p) === cardKey(c)))
        return prev.filter((p) => cardKey(p) !== cardKey(c))
      if (prev.length >= 4) return prev // cap the tray at 4 columns
      setCompareDismissed(false) // re-open the tray when a new vessel is pinned
      return [...prev, c]
    })
  }
  // Shared time scale across pinned vessels → aligned ETD→ETA bars (see who departs sooner / is faster).
  const pinScale = useMemo(() => {
    const ts = pinned
      .flatMap((c) => [
        c.published_departure,
        c.published_arrival,
        c.cutoffs.cy,
        c.cutoffs.doc,
        c.cutoffs.vgm,
      ])
      .filter((s): s is string => Boolean(s))
      .map((s) => new Date(s).getTime())
      .filter((n) => !Number.isNaN(n))
    if (ts.length < 2) return null
    const min = Math.min(...ts)
    return { min, span: Math.max(1, Math.max(...ts) - min) }
  }, [pinned])
  const pinPct = (s?: string | null): number | null => {
    if (!s || !pinScale) return null
    const t = new Date(s).getTime()
    return Number.isNaN(t) ? null : ((t - pinScale.min) / pinScale.span) * 100
  }

  // Full vessel list is gated; first FREE_VESSELS show free.
  const [unlocked, setUnlocked] = useState(false)
  // Carrier-table sort. Default = reliability (the recommendation); click a column to re-sort.
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'rel',
    dir: 'desc',
  })
  // Per-column default direction when first clicked (frequency/reliability high-first; dates/transit low-first).
  const defaultDir: Record<SortKey, 'asc' | 'desc'> = {
    rel: 'desc',
    count: 'desc',
    next: 'asc',
    fastest: 'asc',
  }
  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: defaultDir[key] }
    )
  }

  // Per-carrier comparison rollup — works even on lanes with no reliability yet (the differentiator
  // vs a flat schedule list). Built from the cached sailings, enriched with reliability where known.
  const carrierSummary = useMemo(() => {
    const relByCode = new Map(rows.map((r) => [r.carrier_code, r]))
    const m = new Map<
      string,
      { code: string; name: string; count: number; nextEtd: number | null; fastest: number | null }
    >()
    for (const s of sailings) {
      const e = m.get(s.carrier_code) ?? {
        code: s.carrier_code,
        name: s.carrier_name,
        count: 0,
        nextEtd: null,
        fastest: null,
      }
      e.count++
      if (s.published_departure) {
        const t = new Date(s.published_departure).getTime()
        if (
          !Number.isNaN(t) &&
          t >= Date.now() - 86_400_000 &&
          (e.nextEtd == null || t < e.nextEtd)
        )
          e.nextEtd = t
      }
      // Transit = arrival − departure when both are published; else the carrier's own transit_days
      // (aggregator rows, e.g. weiyun, carry transit_days directly WITHOUT an arrival timestamp — HMM on
      // CNSHA→USLAX has transit_days 15 but no arrival, so the arrival-only path dropped it to "—").
      const d =
        s.published_departure && s.published_arrival
          ? Math.round(
              (new Date(s.published_arrival).getTime() -
                new Date(s.published_departure).getTime()) /
                86_400_000
            )
          : (s.transit_days ?? null)
      if (d != null && d > 0 && (e.fastest == null || d < e.fastest)) e.fastest = d
      m.set(s.carrier_code, e)
    }
    return [...m.values()]
      .map((e) => ({ ...e, rel: relByCode.get(e.code) }))
      .sort((a, b) => {
        const ra = num(a.rel?.on_time_pct)
        const rb = num(b.rel?.on_time_pct)
        if (ra != null && rb != null && ra !== rb) return rb - ra
        if ((ra == null) !== (rb == null)) return ra == null ? 1 : -1
        return b.count - a.count
      })
  }, [sailings, rows])

  // Apply the chosen column sort. Nulls always sink (e.g. carriers with no reliability go last when
  // sorting by reliability, regardless of direction).
  const sortedCarriers = useMemo(() => {
    const val = (c: (typeof carrierSummary)[number]): number | null => {
      switch (sort.key) {
        case 'count':
          return c.count
        case 'next':
          return c.nextEtd
        case 'fastest':
          return c.fastest
        default:
          return num(c.rel?.on_time_pct)
      }
    }
    const mul = sort.dir === 'asc' ? 1 : -1
    return [...carrierSummary].sort((a, b) => {
      const va = val(a)
      const vb = val(b)
      if (va == null && vb == null) return b.count - a.count
      if (va == null) return 1
      if (vb == null) return -1
      return va === vb ? b.count - a.count : (va - vb) * mul
    })
  }, [carrierSummary, sort])

  // Saved lanes — the lines a user wants to keep an eye on. localStorage, no account needed.
  const [favs, setFavs] = useState<{ origin: string; destination: string }[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem('smcp:favLanes') ?? '[]')
      return Array.isArray(v) ? v : []
    } catch {
      return []
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem('smcp:favLanes', JSON.stringify(favs))
    } catch {
      /* private mode / quota — favorites just won't persist */
    }
  }, [favs])
  const isFav = (o?: string, d?: string) =>
    !!o && !!d && favs.some((f) => f.origin === o && f.destination === d)
  function toggleFav(o: string, d: string) {
    if (!o || !d) return
    setFavs((prev) =>
      prev.some((f) => f.origin === o && f.destination === d)
        ? prev.filter((f) => !(f.origin === o && f.destination === d))
        : [{ origin: o, destination: d }, ...prev]
    )
  }

  // Load lanes that have data — both upcoming SCHEDULES (published sailings) and reliability —
  // for the example chips; auto-run the first so the page opens populated.
  useEffect(() => {
    Promise.all([
      fetch(`${PUBLIC_BASE}/schedules/covered`)
        .then((r) => r.json())
        .catch(() => null),
      fetch(`${PUBLIC_BASE}/reliability/covered`)
        .then((r) => r.json())
        .catch(() => null),
    ])
      .then(([sch, rel]) => {
        const schLanes: CoveredLane[] = (sch?.data?.lanes ?? []).map(
          (l: {
            origin: string
            destination: string
            origin_name?: string
            destination_name?: string
            count: number
          }) => ({
            origin: l.origin,
            destination: l.destination,
            origin_name: l.origin_name ?? l.origin,
            destination_name: l.destination_name ?? l.destination,
            carriers: 0,
            obs: l.count,
          })
        )
        // Names are additive on the API side, so tolerate a payload from an older deploy: the lane
        // still renders, just as its locode.
        const relLanes: CoveredLane[] = (rel?.data?.lanes ?? []).map((l: CoveredLane) => ({
          ...l,
          origin_name: l.origin_name ?? l.origin,
          destination_name: l.destination_name ?? l.destination,
        }))
        const seen = new Set<string>()
        const merged = [...schLanes, ...relLanes].filter((l) => {
          const k = `${l.origin}|${l.destination}`
          if (seen.has(k)) return false
          seen.add(k)
          return true
        })
        setCovered(merged)
        setPortNames((prev) =>
          mergeNames(
            prev,
            merged.flatMap((l) => [
              [l.origin, l.origin_name],
              [l.destination, l.destination_name],
            ])
          )
        )
        // Deep link: ?origin=&destination= opens straight to that lane (shareable). Else first covered.
        const sp = new URLSearchParams(window.location.search)
        const uo = sp.get('origin')?.toUpperCase()
        const ud = sp.get('destination')?.toUpperCase()
        // A deep link is an explicit request, so it still runs on its own. Otherwise only SEED the
        // fields: the page used to auto-run the first covered lane, which put a full result table on
        // screen before the user touched anything and made Compare look decorative ("оно
        // автоматически применяется, кнопка не активна" - three people on 2026-08-05). The button
        // must visibly be the thing that produces results.
        if (uo && ud) run(uo, ud, { scroll: false })
        else if (merged[0]) {
          setOrigin(merged[0].origin)
          setDestination(merged[0].destination)
        }
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resultsRef = useRef<HTMLDivElement>(null)

  const [copied, setCopied] = useState(false)
  function copyLink() {
    if (!queried) return
    const url = `${window.location.origin}/compare?origin=${queried.o}&destination=${queried.d}`
    navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1800)
      })
      .catch(() => {})
  }

  async function run(o: string, d: string, opts: { scroll?: boolean } = {}) {
    const O = o.trim().toUpperCase()
    const D = d.trim().toUpperCase()
    setOrigin(O)
    setDestination(D)
    if (!O || !D) return
    setState('loading')
    setQueried({ o: O, d: D })
    setPinned([])
    setUnlocked(false)
    try {
      window.history.replaceState(null, '', `/compare?origin=${O}&destination=${D}`)
    } catch {
      /* non-browser / blocked history — deep link just won't update */
    }
    const qs = `origin=${encodeURIComponent(O)}&destination=${encodeURIComponent(D)}`
    try {
      const [rel, sch, crd] = await Promise.all([
        fetch(`${KEYED_BASE}/reliability/lanes?${qs}`).then((r) => r.json()),
        // `weeks` explicitly, because these endpoints had no upper bound before and this page listed
        // every future sailing; the default of four weeks would silently drop the far half of the
        // table. The flat surface is asked for the widest window it offers and falls back if it
        // refuses (see walkSchedules).
        walkSchedules(qs),
        // THROUGH THE SITE NOW, in the same change that puts a key on cards. Not a moment earlier:
        // while cards was open, routing it here would have replaced every visitor's own address with a
        // Cloudflare colo IP at the API's per-address daily cap — one bucket for the whole site
        // instead of one each — and collapsed its distinct-caller counting, for nothing in return.
        //
        // Cards is asked for less on purpose. It does not page: it caps at 300 rows and says so in
        // `truncated`, which on a busy lane is under four weeks of sailings — so asking it for twelve
        // would hide two thirds of the answer behind a flag this page does not render. Paging that
        // surface is part of its own rework.
        fetch(`${KEYED_BASE}/schedules/cards?${qs}&weeks=4`)
          .then((r) => r.json())
          .catch(() => null),
      ])
      // A deep-linked or saved lane may not be in the covered set, so take its names from the lane
      // responses too. Both endpoints echo the CANONICAL codes, which is what the labels key off.
      setPortNames((prev) =>
        mergeNames(prev, [
          [rel?.data?.origin, rel?.data?.origin_name],
          [rel?.data?.destination, rel?.data?.destination_name],
          [sch?.data?.origin, sch?.data?.origin_name],
          [sch?.data?.destination, sch?.data?.destination_name],
        ])
      )
      setRows(rel?.data?.carriers ?? [])
      // A FAILED FETCH IS NOT AN EMPTY LANE, and until now the page could not tell the reader which it
      // was looking at. walkSchedules returns null when it could not read a first page at all, and
      // that arrived here as `?? []` — the same blank table a lane with no service produces. That is
      // how /public/schedules requiring a key went unnoticed on this page for two days.
      setSailingsFailed(sch === null)
      setSailings(sch?.data?.sailings ?? [])
      setCards(crd?.data?.cards ?? [])
      setLaneCap(crd?.data?.lane_capacity ?? null)
      setBlanks(crd?.data?.suspected_blanks ?? [])
      setState('done')
      // Bring the results into view on an explicit submit. They redraw far below the input panel, so
      // without this the viewport does not change and the press reads as "nothing happened".
      if (opts.scroll)
        requestAnimationFrame(() =>
          resultsRef.current?.scrollIntoView({
            // Smooth unless the reader asked for less motion, in which case jump: the point is that the
            // viewport MOVES, so never trade the movement itself for the animation.
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'auto'
              : 'smooth',
            block: 'start',
          })
        )
    } catch {
      setState('error')
    }
  }

  return (
    <main
      className={`w-full max-w-6xl mx-auto px-5 sm:px-6 pt-12 overflow-x-hidden ${pinned.length > 0 && !compareDismissed ? 'pb-80' : 'pb-10'}`}
    >
      {/* Hero — book §07 anatomy */}
      <header className="text-center mb-14">
        <p
          className="m-0 font-mono text-xs uppercase text-accent"
          style={{ letterSpacing: '0.14em' }}
        >
          Lane comparison
        </p>
        <h1
          className="mx-auto mt-10 mb-0 font-display text-[clamp(30px,5vw,52px)] leading-[1.06] text-[var(--text-primary)]"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          Compare lanes
        </h1>
        <p className="mx-auto mt-10 mb-0 text-base leading-[1.5] text-[var(--text-secondary)]">
          Rank carriers on any lane by 90-day on-time reliability, computed live from observed port
          calls.
        </p>
      </header>

      {/* Instrument — one input system in a panel aligned with the results card below */}
      <div className="mb-8 rounded-2xl border border-[var(--border-strong)] bg-surface-0 px-5 py-6 sm:px-6 print:hidden">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            run(origin, destination, { scroll: true })
          }}
          className="flex flex-wrap items-end justify-center gap-3"
        >
          <PortInput
            label="Origin"
            placeholder="Shanghai or CNSHA"
            seed={origin}
            onChange={setOrigin}
          />
          <span className="pb-2.5 text-[var(--text-muted)]" aria-hidden>
            →
          </span>
          <PortInput
            label="Destination"
            placeholder="Rotterdam or NLRTM"
            seed={destination}
            onChange={setDestination}
          />
          <button
            type="submit"
            disabled={state === 'loading' || !origin.trim() || !destination.trim()}
            className="self-end rounded-lg bg-signal px-6 py-2 font-bold text-on-accent transition-[opacity,transform] duration-100 hover:opacity-90 active:scale-[0.97] active:opacity-80 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-0)]"
          >
            {state === 'loading' ? 'Comparing…' : 'Compare'}
          </button>
        </form>

        {queried && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => toggleFav(queried.o, queried.d)}
              title={isFav(queried.o, queried.d) ? 'Remove from saved' : 'Save this lane'}
              aria-pressed={isFav(queried.o, queried.d)}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                isFav(queried.o, queried.d)
                  ? 'border-signal/60 bg-signal/10 text-signal'
                  : 'border-border text-[var(--text-secondary)] hover:border-signal/50 hover:text-[var(--text-primary)]'
              }`}
            >
              <IconStar filled={isFav(queried.o, queried.d)} />
              {isFav(queried.o, queried.d) ? 'Saved' : 'Save'}
            </button>
            <button
              type="button"
              onClick={copyLink}
              title="Copy a shareable link to this lane"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-[var(--text-secondary)] transition-colors hover:border-signal/50 hover:text-[var(--text-primary)]"
            >
              {copied ? <IconCheck /> : <IconLink />}
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              title="Export this comparison as PDF (print)"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-[var(--text-secondary)] transition-colors hover:border-signal/50 hover:text-[var(--text-primary)]"
            >
              <IconDownload />
              Export PDF
            </button>
          </div>
        )}

        {(favs.length > 0 || covered.length > 0) && (
          <div className="mt-5 space-y-3 border-t border-border pt-5">
            {favs.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <span
                  className="mr-1 font-mono text-xs uppercase text-[var(--text-muted)]"
                  style={{ letterSpacing: '0.1em' }}
                >
                  Saved
                </span>
                {favs.map((l) => {
                  const active = queried?.o === l.origin && queried?.d === l.destination
                  return (
                    <span
                      key={`fav-${l.origin}-${l.destination}`}
                      className={`group flex items-center gap-1 rounded-full border py-1.5 pl-3 pr-1.5 text-xs transition-colors ${
                        active
                          ? 'border-signal/60 bg-signal/10 text-[var(--text-primary)]'
                          : 'border-signal/30 text-[var(--text-secondary)] hover:border-signal/50 hover:text-[var(--text-primary)]'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => run(l.origin, l.destination)}
                        className="flex items-center gap-1.5"
                      >
                        <span className="text-signal">
                          <IconStar filled />
                        </span>
                        <LaneLabel
                          stack
                          origin={l.origin}
                          destination={l.destination}
                          originName={portName(l.origin)}
                          destinationName={portName(l.destination)}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleFav(l.origin, l.destination)}
                        title="Remove"
                        aria-label={`Remove saved lane ${portName(l.origin)} to ${portName(l.destination)}`}
                        className="ml-0.5 rounded-full px-1 text-[var(--text-muted)] hover:bg-white/10 hover:text-[var(--text-primary)]"
                      >
                        ×
                      </button>
                    </span>
                  )
                })}
              </div>
            )}

            {covered.length > 0 && (
              // EXPANDED, THE WAY BACK IS AT THE TOP. Opening this list adds every covered lane we hold —
              // hundreds of them — and the only control was the one that trails the last chip, so the
              // reader who opened it had to scroll to the bottom of the whole set to close it again
              // (reported 2026-09-09). Expanded, the list scrolls inside its own box and the collapse
              // control sits beside the label, where the click that opened it happened.
              <div className="flex flex-col items-center gap-2">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <span
                    className="mr-1 font-mono text-xs uppercase text-[var(--text-muted)]"
                    style={{ letterSpacing: '0.1em' }}
                  >
                    Try a lane
                  </span>
                  {showAllLanes && (
                    <button
                      type="button"
                      onClick={() => setShowAllLanes(false)}
                      className="rounded-full border border-[var(--border-strong)] px-2.5 py-1 font-mono text-xs text-[var(--text-secondary)] hover:border-signal/50 hover:text-[var(--text-primary)]"
                    >
                      collapse
                    </button>
                  )}
                </div>
                <div
                  className={
                    showAllLanes
                      ? 'max-h-72 w-full overflow-y-auto rounded-xl border border-[var(--border-strong)] p-3'
                      : 'w-full'
                  }
                >
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {(showAllLanes ? covered : covered.slice(0, 4)).map((l) => {
                      const active = queried?.o === l.origin && queried?.d === l.destination
                      return (
                        <button
                          key={`${l.origin}-${l.destination}`}
                          type="button"
                          onClick={() => run(l.origin, l.destination)}
                          className={`group flex items-center gap-1.5 rounded-full border py-1.5 pl-3 pr-2 text-xs transition-colors ${
                            active
                              ? 'border-signal/60 bg-signal/10 text-[var(--text-primary)]'
                              : 'border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-signal/50 hover:text-[var(--text-primary)]'
                          }`}
                        >
                          <LaneLabel
                            stack
                            origin={l.origin}
                            destination={l.destination}
                            originName={l.origin_name}
                            destinationName={l.destination_name}
                          />
                          {l.obs > 0 && (
                            <span
                              className={`rounded-full px-1.5 py-px text-[10px] tabular-nums ${
                                active
                                  ? 'bg-signal/20 text-signal'
                                  : 'bg-[var(--surface-2)] text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]'
                              }`}
                            >
                              {l.obs}
                            </span>
                          )}
                        </button>
                      )
                    })}
                    {covered.length > 4 && (
                      <button
                        type="button"
                        onClick={() => setShowAllLanes((s) => !s)}
                        className="px-2 py-1.5 font-mono text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                      >
                        {showAllLanes ? 'collapse' : `+${covered.length - 4} more`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Anchor for the post-submit scroll, plus an explicit statement of WHICH lane is on screen.
          Results redraw well below the fold, so without both the page looked inert on submit. */}
      <div ref={resultsRef} className="scroll-mt-24">
        {queried && (
          <p className="mb-3 flex flex-wrap items-baseline gap-x-1.5 text-[var(--text-secondary)] text-sm">
            <span>{state === 'loading' ? 'Loading' : 'Showing'} results for</span>
            <span className="text-[var(--text-primary)]">
              <LaneLabel
                origin={queried.o}
                destination={queried.d}
                originName={portName(queried.o)}
                destinationName={portName(queried.d)}
              />
            </span>
          </p>
        )}
        {state === 'loading' && <div className="text-[var(--text-muted)] text-sm">Computing…</div>}
      </div>

      {state === 'done' && sailingsFailed && queried && (
        <div className="bg-surface-0 border border-border rounded-xl px-5 py-4 text-sm text-[var(--text-secondary)] flex items-start gap-3">
          <span
            aria-hidden
            className="mt-0.5 shrink-0 inline-block w-1.5 h-1.5 rounded-full bg-[var(--warn)]"
          />
          <span>
            We could not load the published schedule for this lane just now. Reliability below is
            unaffected. Try again in a moment.
          </span>
        </div>
      )}

      {state === 'done' && rows.length === 0 && queried && (
        <div
          className={`bg-surface-0 border border-border rounded-xl px-5 py-4 text-sm text-[var(--text-secondary)] flex items-start gap-3 ${sailings.length > 0 ? '' : 'py-8 text-center justify-center'}`}
        >
          <span
            aria-hidden
            className="mt-0.5 shrink-0 inline-block w-1.5 h-1.5 rounded-full bg-[var(--warn)]"
          />
          <span>
            No 90-day reliability score yet for{' '}
            <span className="text-[var(--text-primary)]">
              <LaneLabel
                origin={queried.o}
                destination={queried.d}
                originName={portName(queried.o)}
                destinationName={portName(queried.d)}
              />
            </span>
            {/* An empty sailings list means two different things, and the advice differs. `sailings`
                is also 0 when the fetch failed, and telling a reader "coverage grows daily, try
                another lane" about a lane we never managed to read is advice about something we do
                not know. The banner above already says the schedule could not be loaded. */}
            {sailingsFailed
              ? '. Reliability appears once enough port calls are observed on this lane.'
              : sailings.length > 0
                ? ', but the published schedule below is live. Reliability appears once enough port calls are observed on this lane.'
                : '. Coverage grows daily as more port calls are observed. Try a lane with data above, or query the full dataset via the API and MCP server.'}
          </span>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-surface-0 border border-[var(--border-strong)] rounded-2xl overflow-hidden">
          {/* ON-TIME % ONLY on the open site (owner 07.10): the delay figures, sample size and berth
              precision are the keyed /public/reliability/lanes answer, and site-api.js does not pass
              them on. */}
          <div className="grid grid-cols-[1.4fr_1fr] gap-4 px-6 py-3 text-xs font-mono uppercase tracking-wide text-[var(--text-secondary)] bg-[var(--surface-1)] border-b border-[var(--border-strong)]">
            <span>Carrier</span>
            <span title="Share of sailings that left within 24h of the carrier's declared date.">
              On-time
            </span>
          </div>
          {rows.map((c, i) => (
            <div
              key={c.carrier_code}
              className="grid grid-cols-[1.4fr_1fr] gap-4 px-6 py-4 items-center border-b border-border last:border-0 odd:bg-white/[0.015]"
            >
              <div>
                <div className="font-medium flex items-center gap-2">
                  {i === 0 && !c.provisional && (
                    <span className="text-[10px] font-mono bg-signal/15 text-signal rounded px-1.5 py-0.5">
                      BEST
                    </span>
                  )}
                  {c.provisional && (
                    <span className="text-[10px] font-mono bg-[var(--text-muted)]/15 text-[var(--text-muted)] rounded px-1.5 py-0.5">
                      EARLY DATA
                    </span>
                  )}
                  {c.carrier_name}
                </div>
                <div className="text-xs font-mono text-[var(--text-muted)]">{c.carrier_code}</div>
              </div>
              <ReliabilityBar pct={num(c.on_time_pct) ?? 0} />
            </div>
          ))}
        </div>
      )}

      {state === 'done' && carrierSummary.length > 1 && (
        <div className="mt-8">
          <div className="flex items-baseline justify-between flex-wrap gap-x-3 mb-1">
            <h2 className="font-display font-bold text-2xl">Carrier comparison</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              <LaneLabel
                flags
                origin={queried?.o}
                destination={queried?.d}
                originName={portName(queried?.o)}
                destinationName={portName(queried?.d)}
              />
            </p>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-3">
            {carrierSummary.length} carriers on this lane, ranked by 90-day reliability where
            observed, then sailing frequency: the side-by-side a plain schedule search doesn't give
            you.
          </p>
          <div className="bg-surface-0 border border-[var(--border-strong)] rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[1.6fr_0.7fr_0.9fr_0.8fr_1fr] gap-3 px-5 py-3 text-xs font-mono uppercase tracking-wide text-[var(--text-secondary)] bg-[var(--surface-1)] border-b border-[var(--border-strong)]">
              <span>Carrier</span>
              {(
                [
                  ['count', 'Sailings', 'text-right justify-end'],
                  ['next', 'Next departure', ''],
                  ['fastest', 'Fastest', ''],
                  ['rel', 'On-time (90d)', ''],
                ] as [SortKey, string, string][]
              ).map(([key, label, cls]) => {
                const active = sort.key === key
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleSort(key)}
                    className={`flex items-center gap-1.5 uppercase tracking-wide transition-colors hover:text-[var(--text-primary)] ${cls} ${
                      active ? 'text-[var(--text-primary)]' : ''
                    }`}
                    title={`Sort by ${label}`}
                  >
                    {/* Shoreditch red-dot: tiny accent dot marks the active sort */}
                    <span
                      aria-hidden
                      className={`inline-block h-1.5 w-1.5 rounded-full ${active ? 'bg-signal' : 'bg-transparent'}`}
                    />
                    {label}
                    {active && (
                      <span aria-hidden className="text-signal">
                        {sort.dir === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
            {sortedCarriers.map((c, i) => (
              <div
                key={c.code}
                className="grid grid-cols-[1.6fr_0.7fr_0.9fr_0.8fr_1fr] gap-3 px-5 py-3 items-center border-b border-border last:border-0 text-sm odd:bg-white/[0.015]"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {i === 0 &&
                      sort.key === 'rel' &&
                      sort.dir === 'desc' &&
                      num(c.rel?.on_time_pct) != null &&
                      !c.rel?.provisional && (
                        <span className="text-[10px] font-mono bg-signal/15 text-signal rounded px-1.5 py-0.5">
                          BEST
                        </span>
                      )}
                    <span className="truncate text-[var(--text-primary)]">{c.name}</span>
                  </div>
                  <div className="text-xs font-mono text-[var(--text-muted)]">{c.code}</div>
                </div>
                <span className="text-right font-mono tabular-nums text-[var(--text-primary)]">
                  {c.count}
                </span>
                <span className="font-mono tabular-nums text-[var(--text-secondary)]">
                  {c.nextEtd ? fmtDate(c.nextEtd) : '—'}
                </span>
                <span className="font-mono tabular-nums text-[var(--text-secondary)]">
                  {c.fastest != null ? `${c.fastest}d` : '—'}
                </span>
                <span>
                  {num(c.rel?.on_time_pct) != null ? (
                    // On-time % only (owner 07.10): the delay line is the keyed answer.
                    <ReliabilityBar pct={num(c.rel?.on_time_pct) ?? 0} />
                  ) : (
                    <span className="font-mono text-xs text-[var(--text-faint)]">no data yet</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {state === 'done' && cards.length > 0 && (
        <LaneTimeline cards={unlocked ? cards : cards.slice(0, FREE_VESSELS)} />
      )}

      {cards.length > 0 && (
        <div className="mt-8">
          <div className="flex items-baseline justify-between flex-wrap gap-x-3 mb-3">
            <h2 className="font-display font-bold text-2xl">Upcoming sailings</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              <span className="font-mono text-[var(--text-primary)] tabular-nums">
                {cards.length}
              </span>{' '}
              vessels ·{' '}
              <span className="text-[var(--text-secondary)]">
                <LaneLabel
                  flags
                  origin={queried?.o}
                  destination={queried?.d}
                  originName={portName(queried?.o)}
                  destinationName={portName(queried?.d)}
                />
              </span>
            </p>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            One card per physical vessel. Slot-sharing carriers are grouped inside, so a ship sold
            by three lines shows once, not three times.
          </p>
          {(laneCap?.total_teu || blanks.length > 0) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {laneCap?.total_teu && (
                <span className="text-xs rounded-lg px-3 py-1.5 bg-[var(--surface-1)] border border-border text-[var(--text-secondary)]">
                  Capacity{' '}
                  <span className="font-mono text-[var(--text-primary)] tabular-nums">
                    {laneCap.total_teu.toLocaleString()}
                  </span>{' '}
                  TEU over window
                  {laneCap.teu_per_week ? (
                    <>
                      {' '}
                      ·{' '}
                      <span className="font-mono text-[var(--text-primary)] tabular-nums">
                        {laneCap.teu_per_week.toLocaleString()}
                      </span>{' '}
                      TEU/wk
                    </>
                  ) : null}
                  {laneCap.estimated ? (
                    <span className="text-[var(--text-muted)]"> (incl. est)</span>
                  ) : null}
                </span>
              )}
              {blanks.length > 0 && (
                <span
                  className="text-xs rounded-lg px-3 py-1.5 border"
                  style={{
                    background: 'color-mix(in srgb, var(--warn) 10%, transparent)',
                    borderColor: 'color-mix(in srgb, var(--warn) 40%, transparent)',
                    color: 'var(--warn)',
                  }}
                  title={blanks
                    .slice(0, 6)
                    .map((b) => `${b.service}: ${b.gap_days}d gap (${b.after}→${b.next})`)
                    .join('\n')}
                >
                  {blanks.length} suspected blank sailing{blanks.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3">
            {(unlocked ? cards : cards.slice(0, FREE_VESSELS)).map((card, i) => (
              <SailingCard
                key={`${card.vessel_imo ?? card.vessel_name}-${card.published_departure}-${card.voyage_number}-${i}`}
                card={card}
                pinned={isPinned(card)}
                onTogglePin={() => togglePin(card)}
                unlocked={unlocked}
              />
            ))}
          </div>
          {!unlocked && cards.length > FREE_VESSELS && (
            <div className="mt-4 rounded-2xl border border-[var(--border-strong)] bg-[var(--surface-1)] px-6 py-5 text-center print:hidden">
              <p className="text-[var(--text-primary)] font-medium">
                {cards.length - FREE_VESSELS} more vessels on this lane
              </p>
              <p className="text-sm text-[var(--text-secondary)] mt-1 mb-4">
                You're seeing the first {FREE_VESSELS}. Subscribe for the full schedule, every lane,
                and your own API +{' '}
                <a
                  href="/mcp"
                  className="underline underline-offset-2 hover:text-[var(--text-primary)]"
                >
                  MCP
                </a>{' '}
                access.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <a
                  href="/pricing"
                  className="bg-signal text-white font-bold px-5 py-2 rounded-lg hover:opacity-90"
                >
                  See plans
                </a>
                <button
                  type="button"
                  onClick={() => setUnlocked(true)}
                  className="text-sm text-[var(--text-secondary)] underline underline-offset-4 hover:text-[var(--text-primary)]"
                >
                  Preview all {cards.length} here
                </button>
              </div>
            </div>
          )}
          {unlocked && cards.length > FREE_VESSELS && (
            <p className="text-xs text-[var(--text-muted)] mt-3">
              Showing all {cards.length} vessels (preview). Programmatic access via the API and{' '}
              <a
                href="/mcp"
                className="underline underline-offset-2 hover:text-[var(--text-primary)]"
              >
                MCP server
              </a>
              .
            </p>
          )}
        </div>
      )}

      {cards.length === 0 && sailings.length > 0 && (
        <div className="mt-8">
          <div className="flex items-baseline justify-between flex-wrap gap-x-3 mb-3">
            <h2 className="font-display font-bold text-2xl">Upcoming sailings</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              <span className="font-mono text-[var(--text-primary)] tabular-nums">
                {sailings.length}
              </span>{' '}
              cached departures ·{' '}
              <span className="text-[var(--text-secondary)]">
                <LaneLabel
                  flags
                  origin={queried?.o}
                  destination={queried?.d}
                  originName={portName(queried?.o)}
                  destinationName={portName(queried?.d)}
                />
              </span>
            </p>
          </div>
          <div className="bg-surface-0 border border-[var(--border-strong)] rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[1.3fr_1.4fr_0.9fr_0.9fr_0.9fr] gap-4 px-6 py-3 text-xs font-mono uppercase tracking-wide text-[var(--text-secondary)] bg-[var(--surface-1)] border-b border-[var(--border-strong)]">
              <span>Carrier</span>
              <span>Vessel / Voyage</span>
              <span>ETD</span>
              <span>ETA</span>
              <span>CY cutoff</span>
            </div>
            {sailings.slice(0, 60).map((s, i) => (
              <div
                key={`${s.carrier_code}-${s.vessel_name}-${s.published_departure}-${i}`}
                className="grid grid-cols-[1.3fr_1.4fr_0.9fr_0.9fr_0.9fr] gap-4 px-6 py-3.5 items-center border-b border-border last:border-0 text-sm odd:bg-white/[0.015]"
              >
                <div>
                  <div className="font-medium text-[var(--text-primary)]">{s.carrier_name}</div>
                  <div className="text-xs font-mono text-[var(--text-muted)]">{s.carrier_code}</div>
                </div>
                <div className="min-w-0">
                  <div className="truncate text-[var(--text-primary)]">
                    {s.vessel_name ?? <span className="text-[var(--text-faint)]">—</span>}
                  </div>
                  <div className="text-xs font-mono text-[var(--text-muted)] truncate">
                    {[s.service_name, s.voyage_number].filter(Boolean).join(' · ') || ''}
                  </div>
                </div>
                <span className="font-mono text-[var(--text-primary)] tabular-nums">
                  {fmtDate(s.published_departure)}
                </span>
                <span className="font-mono text-[var(--text-secondary)] tabular-nums">
                  {fmtDate(s.published_arrival)}
                </span>
                {/* An empty cut-off has two meanings and they are not interchangeable: this carrier
                    published none, or we rebuilt the sailing from a slot partner and never held this
                    carrier's. The API says which; say it here rather than showing one blank for both. */}
                <span className="font-mono text-[var(--text-muted)] tabular-nums">
                  {s.cy_cutoff ? (
                    fmtDate(s.cy_cutoff)
                  ) : s.cutoff_status === 'not_held' ? (
                    <span
                      className="text-[var(--text-faint)] italic text-xs"
                      title={`This sailing came to us from another line selling the same ship, so we never held ${s.carrier_name ?? s.carrier_code}'s own cut-off.`}
                    >
                      not held
                    </span>
                  ) : (
                    <span
                      className="text-[var(--text-faint)]"
                      title={
                        s.cutoff_status === 'not_published'
                          ? `We hold ${s.carrier_name ?? s.carrier_code}'s own schedule for this sailing and it carries no cut-off time.`
                          : undefined
                      }
                    >
                      —
                    </span>
                  )}
                </span>
              </div>
            ))}
            {sailings.length > 60 && (
              <div className="px-6 py-3 text-xs text-[var(--text-muted)] bg-[var(--surface-1)]/40 border-t border-border">
                Showing first 60 of {sailings.length}. Full schedule via the API and MCP server.
              </div>
            )}
          </div>
        </div>
      )}

      {pinned.length > 0 && !compareDismissed && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[var(--border-strong)] bg-[var(--surface-1)]/95 backdrop-blur print:hidden">
          {/* close — keeps the pins, just hides the tray (Shoreditch ✕) */}
          <button
            type="button"
            onClick={() => setCompareDismissed(true)}
            aria-label="Close comparison tray"
            className="absolute right-3 top-2 z-10 flex h-6 w-6 items-center justify-center rounded border border-[var(--border)] font-mono text-[13px] leading-none text-[var(--text-muted)] hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]"
          >
            ×
          </button>
          <div className="mx-auto max-w-6xl px-5 py-3">
            <div className="flex items-stretch gap-3 overflow-x-auto pr-8">
              <div className="flex shrink-0 flex-col justify-between py-1 pr-2">
                <div>
                  <div className="font-display font-bold text-sm">Compare</div>
                  <div className="font-mono text-[11px] text-[var(--text-muted)]">
                    {pinned.length}/4 pinned
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPinned([])}
                  className="mt-2 text-left text-[11px] text-[var(--text-muted)] underline underline-offset-2 hover:text-[var(--text-primary)]"
                >
                  clear
                </button>
              </div>
              {pinned.map((c) => {
                const rel =
                  c.partners.find((p) => p.reliability_pct != null)?.reliability_pct ?? null
                const cut = [c.cutoffs.cy, c.cutoffs.doc, c.cutoffs.vgm]
                  .filter(Boolean)
                  .sort()[0] as string | undefined
                return (
                  <div
                    key={cardKey(c)}
                    className="relative w-44 shrink-0 rounded-lg border border-border bg-surface-0 px-3 py-2"
                  >
                    <button
                      type="button"
                      onClick={() => togglePin(c)}
                      aria-label="Remove from comparison"
                      className="absolute right-1.5 top-1.5 rounded px-1 text-[var(--text-muted)] hover:bg-white/10 hover:text-[var(--text-primary)]"
                    >
                      ×
                    </button>
                    <div className="truncate pr-4 text-sm font-medium text-[var(--text-primary)]">
                      {c.vessel_name ?? '—'}
                    </div>
                    <div className="mb-1.5 truncate font-mono text-[10px] text-[var(--text-muted)]">
                      {c.partner_count > 1
                        ? `${c.partner_count} lines`
                        : (c.partners[0]?.carrier_code ?? '')}
                      {c.service_name ? ` · ${c.service_name}` : ''}
                    </div>
                    {/* shared-scale voyage bar: ETD ▸———— ETA, all pinned vessels on one timeline */}
                    {(() => {
                      const l = pinPct(c.published_departure)
                      const r = pinPct(c.published_arrival)
                      if (l == null || r == null) return null
                      const cutL = pinPct(cut ?? null)
                      return (
                        <div
                          className="relative mb-2 h-1.5 rounded-full bg-[var(--surface-2)]"
                          title={`${fmtDate(c.published_departure)} → ${fmtDate(c.published_arrival)}`}
                        >
                          <div
                            className="absolute h-full rounded-full bg-signal/70"
                            style={{ left: `${l}%`, width: `${Math.max(2, r - l)}%` }}
                          />
                          {cutL != null && (
                            <div
                              className="absolute -top-[3px] h-[12px] w-px bg-[var(--warn)]"
                              style={{ left: `${cutL}%` }}
                              title={`cutoff ${fmtDate(cut ?? null)}`}
                            />
                          )}
                        </div>
                      )
                    })()}
                    <dl className="space-y-0.5 font-mono text-[11px] tabular-nums">
                      <div className="flex justify-between gap-2">
                        <dt className="text-[var(--text-muted)]">ETD</dt>
                        <dd className="text-[var(--text-secondary)]">
                          {fmtDate(c.published_departure)}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt className="text-[var(--text-muted)]">transit</dt>
                        <dd className="text-[var(--text-primary)]">
                          {c.transit_days != null ? `${c.transit_days}d` : '—'}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt className="text-[var(--text-muted)]">TEU</dt>
                        <dd className="text-[var(--text-secondary)]">
                          {c.capacity_teu ? c.capacity_teu.toLocaleString() : '—'}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt className="text-[var(--text-muted)]">on-time</dt>
                        <dd className="text-[var(--text-secondary)]">
                          {rel != null ? `${Math.round(rel)}%` : '—'}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt className="text-[var(--text-muted)]">cutoff</dt>
                        <dd className="text-[var(--text-secondary)]">{cut ? fmtDate(cut) : '—'}</dd>
                      </div>
                    </dl>
                  </div>
                )
              })}
            </div>

            {/* Infographic — consolidated ARRIVAL comparison: every pinned voyage on ONE shared
                timeline so you see at a glance who gets the box there first and what you trade for
                it. Fastest transit + earliest arrival flagged; delta = days vs the earliest arriver. */}
            {pinned.length >= 2 &&
              pinScale &&
              (() => {
                const arrMs = (c: SailingCardData) =>
                  c.published_arrival ? new Date(c.published_arrival).getTime() : Number.NaN
                const withArr = pinned.filter((c) => !Number.isNaN(arrMs(c)))
                if (withArr.length < 2) return null
                const earliestArr = Math.min(...withArr.map(arrMs))
                const transits = pinned
                  .map((c) => c.transit_days)
                  .filter((t): t is number => t != null)
                const fastest = transits.length ? Math.min(...transits) : null
                const rows = [...pinned].sort((a, b) => arrMs(a) - arrMs(b))
                return (
                  <div className="mt-3 border-t border-[var(--border)] pt-2.5">
                    <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                      Arrival comparison · shared timeline
                    </div>
                    <div className="space-y-1.5">
                      {rows.map((c) => {
                        const l = pinPct(c.published_departure)
                        const r = pinPct(c.published_arrival)
                        const isFastest = fastest != null && c.transit_days === fastest
                        // "first" = the EXACT earliest arrival timestamp (ETAs are timestamp-level),
                        // not merely the same rounded day — two sailings ~12h apart must not tie.
                        const isFirst = !Number.isNaN(arrMs(c)) && arrMs(c) === earliestArr
                        const deltaD = !Number.isNaN(arrMs(c))
                          ? Math.round((arrMs(c) - earliestArr) / 86_400_000)
                          : null
                        return (
                          <div key={cardKey(c)} className="flex items-center gap-2">
                            <div className="w-24 shrink-0 truncate font-mono text-[10px] text-[var(--text-secondary)]">
                              {c.vessel_name ?? '—'}
                            </div>
                            <div className="relative h-2 flex-1 rounded-full bg-[var(--surface-2)]">
                              {l != null && r != null && (
                                <div
                                  className={`absolute h-full rounded-full ${isFastest ? 'bg-signal' : 'bg-signal/40'}`}
                                  style={{ left: `${l}%`, width: `${Math.max(2, r - l)}%` }}
                                />
                              )}
                              {r != null && (
                                <div
                                  className="absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--text-primary)]"
                                  style={{ left: `${r}%` }}
                                />
                              )}
                            </div>
                            <div className="w-[120px] shrink-0 text-right font-mono text-[10px] tabular-nums text-[var(--text-secondary)]">
                              {c.transit_days != null ? `${c.transit_days}d` : '—'} ·{' '}
                              {fmtDate(c.published_arrival)}
                              {deltaD != null && deltaD > 0 && (
                                <span className="text-[var(--text-muted)]"> +{deltaD}d</span>
                              )}
                              {isFirst && <span className="text-signal"> first</span>}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })()}
          </div>
        </div>
      )}

      <p className="text-xs text-[var(--text-muted)] mt-6 print:hidden">
        Schedules are cached published sailings from the carriers' own systems; reliability figures
        are computed from observed actual-vs-scheduled port calls, with thin lanes flagged{' '}
        <span className="font-mono">EARLY DATA</span>. Full coverage when you query via your own API
        + MCP keys. Booked it? Track the box end-to-end on{' '}
        <a
          href="https://trackingmcp.com"
          className="underline underline-offset-2 hover:text-[var(--text-primary)]"
        >
          TrackingMCP
        </a>
        : separate product, same login.
      </p>

      <div className="print:hidden">
        <Faq id="compare" items={COMPARE_FAQ} heading="Comparing ocean carriers: FAQ" />
      </div>
    </main>
  )
}
